import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../api/client';

const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const GIORNI_LBL = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

const meseCorrente = () => new Date().toISOString().slice(0, 7);
const fmtDataEstesa = (ymd) => new Date(ymd + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: 'long' });

export default function CalendarioMensileStaff() {
  const { user } = useAuth();
  const [mese, setMese] = useState(meseCorrente());
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [giornoSel, setGiornoSel] = useState(new Date().toISOString().slice(0, 10));
  const [modalita, setModalita] = useState([]);

  // interazione sul giorno selezionato
  const [selezione, setSelezione] = useState(null); // voce libera scelta
  const [bloccoAttivo, setBloccoAttivo] = useState(false);
  const [bloccoNota, setBloccoNota] = useState('');
  const [dettaglio, setDettaglio] = useState(null); // voce occupata/bloccata
  const [step, setStep] = useState('lista'); // lista | form | ok
  const [form, setForm] = useState({ nome: '', email: '', telefono: '', modalita_pagamento_id: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [okData, setOkData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/campi/calendario-mese', { params: { mese } }); setDati(r.data); }
    catch { setDati(null); }
    finally { setLoading(false); }
  }, [mese]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/campi/modalita-pagamento').then(r => setModalita(r.data.filter(m => m.attiva))).catch(() => {}); }, []);

  const resetInterazione = () => { setSelezione(null); setBloccoAttivo(false); setBloccoNota(''); setDettaglio(null); setStep('lista'); setErr(''); setOkData(null); };

  const navMese = (dir) => {
    const [a, m] = mese.split('-').map(Number);
    const d = new Date(a, m - 1 + dir, 1);
    setMese(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const selezionaGiorno = (ymd) => {
    setGiornoSel(ymd === giornoSel ? null : ymd);
    resetInterazione();
  };

  const clickVoce = (v) => {
    resetInterazione();
    if (!v.stato) setSelezione(v);
    else setDettaglio(v);
  };

  const vaiAlForm = () => { setStep('form'); setForm(f => ({ ...f, nome: user?.nome || '', email: user?.email || '' })); };

  const confermaBlocco = async () => {
    setSaving(true); setErr('');
    try {
      await api.post('/campi/blocca', { slot_id: selezione.slot_id, data: giornoSel, note: bloccoNota });
      resetInterazione(); load();
    } catch (e) { setErr(e.response?.data?.error || 'Errore'); }
    finally { setSaving(false); }
  };

  const confermaPrenota = async () => {
    if (!form.nome.trim() || !form.email.trim()) { setErr('Inserisci nome ed email.'); return; }
    setSaving(true); setErr('');
    try {
      const r = await api.post('/campi/prenota', {
        slot_id: selezione.slot_id, data: giornoSel,
        nome: form.nome, email: form.email, telefono: form.telefono,
        modalita_pagamento_id: form.modalita_pagamento_id || undefined,
      });
      setOkData(r.data); setStep('ok'); load();
    } catch (e) { setErr(e.response?.data?.error || 'Errore'); }
    finally { setSaving(false); }
  };

  const togglePagato = async () => {
    try { await api.patch(`/campi/${dettaglio.prenotazione_id}/pagato`, { pagato: !dettaglio.pagato }); setDettaglio(d => ({ ...d, pagato: !d.pagato })); load(); }
    catch (e) { alert(e.response?.data?.error || 'Errore'); }
  };
  const annullaPren = async () => {
    if (!window.confirm('Annullare questa prenotazione?')) return;
    try { await api.post(`/campi/${dettaglio.prenotazione_id}/annulla`); setDettaglio(null); load(); }
    catch (e) { alert(e.response?.data?.error || 'Errore'); }
  };
  const sblocca = async () => {
    try { await api.delete(`/campi/blocco/${dettaglio.prenotazione_id}`); setDettaglio(null); load(); }
    catch (e) { alert(e.response?.data?.error || 'Errore'); }
  };

  const oggi = new Date().toISOString().slice(0, 10);
  const [annoNum, meseNum] = mese.split('-').map(Number);
  const primoGiorno = new Date(annoNum, meseNum - 1, 1);
  const offset = (primoGiorno.getDay() + 6) % 7;
  const celle = [];
  for (let i = 0; i < offset; i++) celle.push(null);
  (dati?.giorni || []).forEach(g => celle.push(g));

  const giornoDati = dati?.giorni.find(g => g.data === giornoSel);

  const inp = { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '9px 12px', color: 'var(--text)', fontSize: 'var(--font-size-sm)', outline: 'none', width: '100%', boxSizing: 'border-box' };
  const lab = { fontSize: 'var(--font-size-xs)', letterSpacing: '2px', fontWeight: 700, color: '#555', display: 'block', marginBottom: '4px' };

  return (
    <div style={s.card}>
      <div style={s.navBar}>
        <button style={s.navBtn} onClick={() => navMese(-1)}><ChevronLeft size={16} /></button>
        <div style={s.weekLabel}>{MESI[meseNum - 1]} {annoNum}</div>
        <button style={s.navBtn} onClick={() => navMese(1)}><ChevronRight size={16} /></button>
      </div>

      <div style={s.legenda}>
        <span><i style={s.legDot('#F4600C18', '#F4600C55')} />Libero</span>
        <span><i style={s.legDot('#E8E8ED', '#D1D1D6')} />Occupato</span>
        <span><i style={s.legDot('#EF444418', '#EF444450')} />Bloccato</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', opacity: loading && dati ? 0.6 : 1, transition: 'opacity 0.12s' }}>
        {GIORNI_LBL.map((l, i) => <div key={i} style={s.dayHead}>{l}</div>)}
        {celle.map((g, i) => g ? (
          <button key={g.data} type="button" onClick={() => selezionaGiorno(g.data)}
            style={{ ...s.dayCell, background: g.data === giornoSel ? '#F4600C15' : 'var(--bg-card)', border: g.data === giornoSel ? '1.5px solid #F4600C70' : (g.data === oggi ? '1px solid var(--border2)' : '1px solid var(--border)') }}>
            <div style={{ ...s.dayNum, color: g.data === oggi ? '#F4600C' : '#ccc', pointerEvents: 'none' }}>{Number(g.data.slice(8, 10))}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%', pointerEvents: 'none' }}>
              {g.voci.slice(0, 3).map(v => (
                <div key={v.slot_id} style={{ ...s.chip, background: v.stato === 'CONFERMATA' ? '#E8E8ED' : v.stato === 'BLOCCATA' ? '#FEE4E2' : '#FFEADB', color: v.stato === 'CONFERMATA' ? '#3A3A3C' : v.stato === 'BLOCCATA' ? '#B42318' : '#9A3A06' }}>
                  {v.ora_inizio}{v.nome ? ` ${v.nome.split(' ')[0]}` : ''}
                </div>
              ))}
              {g.voci.length > 3 && <div style={s.chipMore}>+{g.voci.length - 3}</div>}
              {g.voci.length === 0 && <div style={s.chipVuoto}>—</div>}
            </div>
          </button>
        ) : <div key={'e' + i} />)}
      </div>

      {giornoSel && (
        <div style={s.dayDetail}>
          <div style={s.dayDetailTitle}>{fmtDataEstesa(giornoSel)}</div>

          {step === 'form' && selezione && (
            <div style={s.formBox}>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)', marginBottom: '12px' }}>
                <strong>{selezione.sede_nome} — {selezione.campo_nome}</strong> · {selezione.ora_inizio}–{selezione.ora_fine} · € {selezione.prezzo.toFixed(2)}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div><label style={lab}>NOME E COGNOME *</label><input style={inp} value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} /></div>
                <div><label style={lab}>EMAIL *</label><input style={inp} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                <div><label style={lab}>TELEFONO</label><input style={inp} value={form.telefono} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))} /></div>
                <div>
                  <label style={lab}>MODALITÀ DI PAGAMENTO</label>
                  <select style={inp} value={form.modalita_pagamento_id} onChange={e => setForm(f => ({ ...f, modalita_pagamento_id: e.target.value }))}>
                    <option value="">Da definire</option>
                    {modalita.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
                  </select>
                </div>
              </div>
              {err && <div style={s.errBox}>{err}</div>}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setStep('lista')} style={s.btnGhost}>← Indietro</button>
                <button onClick={confermaPrenota} disabled={saving} style={{ ...s.btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'Invio...' : 'Prenota ora'}</button>
              </div>
            </div>
          )}

          {step === 'ok' && okData && (
            <div style={s.formBox}>
              <div style={{ color: '#4ade80', fontWeight: 800, marginBottom: '8px' }}>✓ Prenotazione creata — codice {okData.codice}</div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: '#aaa', marginBottom: '10px' }}>Importo € {okData.prezzo.toFixed(2)} · Causale: Affitto campo {okData.nome} {okData.codice}</div>
              {okData.istruzioniPagamento && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text)', whiteSpace: 'pre-wrap', marginBottom: '10px' }}>{okData.istruzioniPagamento}</div>}
              {okData.messaggioConferma && <div style={{ fontSize: 'var(--font-size-xs)', color: '#888', whiteSpace: 'pre-wrap', marginBottom: '10px' }}>{okData.messaggioConferma}</div>}
              <button onClick={resetInterazione} style={s.btnPri}>Chiudi</button>
            </div>
          )}

          {step === 'lista' && (
            <>
              {!giornoDati || giornoDati.voci.length === 0 ? (
                <div style={{ color: '#666', fontSize: 'var(--font-size-sm)' }}>Nessuno slot proponibile in questo giorno.</div>
              ) : giornoDati.voci.map(v => {
                const selezionata = selezione && selezione.slot_id === v.slot_id;
                const dettagliata = dettaglio && dettaglio.slot_id === v.slot_id;
                return (
                  <div key={v.slot_id}>
                    <button type="button" onClick={() => clickVoce(v)} style={{ ...s.rigaSlot, borderColor: (selezionata || dettagliata) ? '#F4600C70' : 'var(--border)' }}>
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <strong>{v.ora_inizio}–{v.ora_fine}</strong> · {v.sede_nome} — {v.campo_nome} · € {v.prezzo.toFixed(2)}
                        {v.stato === 'CONFERMATA' && <div style={{ fontSize: '12px', color: '#aaa', marginTop: '2px' }}>{v.nome} · {v.codice} {v.pagato ? '· pagato' : '· non pagato'}</div>}
                        {v.stato === 'BLOCCATA' && <div style={{ fontSize: '12px', color: '#B42318', marginTop: '2px' }}>Occupato{v.note ? ` — ${v.note}` : ''}</div>}
                        {!v.stato && <div style={{ fontSize: '12px', color: '#9A3A06', marginTop: '2px' }}>Libero</div>}
                      </div>
                    </button>

                    {selezionata && !dettagliata && (
                      <div style={s.azioniBox}>
                        {!bloccoAttivo ? (
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => setBloccoAttivo(true)} style={s.btnGhost}>Segna occupato</button>
                            <button onClick={vaiAlForm} style={s.btnPri}>Prenota →</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                            <input autoFocus style={{ ...inp, flex: 1, minWidth: '200px' }} placeholder="Perché è occupato? (manutenzione, torneo, uso interno...)" value={bloccoNota} onChange={e => setBloccoNota(e.target.value)} />
                            <button onClick={() => setBloccoAttivo(false)} style={s.btnGhost}>Annulla</button>
                            <button onClick={confermaBlocco} disabled={saving} style={{ ...s.btnPri, opacity: saving ? 0.6 : 1 }}>Conferma</button>
                          </div>
                        )}
                        {err && <div style={s.errBox}>{err}</div>}
                      </div>
                    )}

                    {dettagliata && v.stato === 'BLOCCATA' && (
                      <div style={s.azioniBox}>
                        <button onClick={sblocca} style={{ ...s.btnGhost, color: '#4ade80', borderColor: '#22C55E40' }}>Libera slot</button>
                      </div>
                    )}
                    {dettagliata && v.stato === 'CONFERMATA' && (
                      <div style={s.azioniBox}>
                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)', marginBottom: '8px' }}>{v.email} {v.telefono ? `· ${v.telefono}` : ''}</div>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                          <button onClick={togglePagato} style={{ ...s.btnGhost, color: dettaglio.pagato ? '#4ade80' : '#888' }}>{dettaglio.pagato ? 'Pagato ✓' : 'Segna come pagato'}</button>
                          <button onClick={annullaPren} style={{ ...s.btnGhost, color: '#f1948a', borderColor: '#EF444440' }}>Annulla prenotazione</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const s = {
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '18px' },
  navBar: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' },
  navBtn: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text)', flexShrink: 0 },
  weekLabel: { flex: 1, textAlign: 'center', fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight: 800, letterSpacing: '1px', color: 'var(--text)', textTransform: 'uppercase' },
  legenda: { display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '10px', fontSize: 'var(--font-size-xs)', color: '#888' },
  legDot: (bg, border) => ({ width: '11px', height: '11px', borderRadius: '3px', background: bg, border: `1px solid ${border || bg}`, display: 'inline-block', marginRight: '5px', verticalAlign: 'middle' }),
  dayHead: { textAlign: 'center', fontSize: '10px', color: '#666', fontWeight: 700, paddingBottom: '2px' },
  dayCell: { borderRadius: '8px', padding: '5px 4px', cursor: 'pointer', minHeight: '68px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' },
  dayNum: { fontSize: 'var(--font-size-xs)', fontWeight: 700 },
  chip: { fontSize: '9px', fontWeight: 700, borderRadius: '3px', padding: '1px 3px', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  chipMore: { fontSize: '9px', color: '#666', fontWeight: 700 },
  chipVuoto: { fontSize: '9px', color: 'var(--text-dim)' },
  dayDetail: { marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #222', display: 'flex', flexDirection: 'column', gap: '8px' },
  dayDetailTitle: { fontFamily: "'Barlow Condensed'", fontWeight: 800, letterSpacing: '0.5px', color: '#F4600C', fontSize: 'var(--font-size-md)', textTransform: 'capitalize', marginBottom: '4px' },
  rigaSlot: { display: 'flex', alignItems: 'center', width: '100%', background: 'var(--bg-input)', border: '1.5px solid var(--border)', borderRadius: '8px', padding: '10px 14px', color: 'var(--text)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' },
  azioniBox: { background: 'var(--bg)', border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '12px 14px', marginTop: '-8px' },
  formBox: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '14px' },
  btnGhost: { background: 'transparent', border: '1px solid var(--border2)', borderRadius: '6px', color: 'var(--text)', padding: '8px 16px', cursor: 'pointer', fontWeight: 700, fontSize: 'var(--font-size-xs)' },
  btnPri: { background: '#F4600C', border: 'none', borderRadius: '6px', color: '#fff', padding: '8px 18px', cursor: 'pointer', fontWeight: 800, fontSize: 'var(--font-size-xs)' },
  errBox: { marginTop: '8px', background: '#EF444420', border: '1px solid #EF444440', color: '#f1948a', borderRadius: '6px', padding: '6px 10px', fontSize: 'var(--font-size-xs)' },
};
